import _ from 'lodash';
import { useEffect, useState, useCallback } from 'react';
import { useApolloClient } from '@apollo/client';

import {
  DASHBOARD_GET_PROFILE,
  DASHBOARD_GET_PRODUCT_EDE_APP,
  DASHBOARD_GET_LATEST_PRODUCT_APP,
  DASHBOARD_GET_ENROLLMENT_STATUS,
  DASHBOARD_GET_ALL_PRODUCT_APPS,
  DASHBOARD_GET_PRODUCT_APP,
} from 'client/queries';
import { getStatusType } from 'routes/dashboard/components/edePhase3Card';
import { useRegrantPermissionMutation } from '../../routes/marketplace-ede/hooks/edeApplication';
import { getStatusCodes } from './utils/errorHandling';

const skippableQuery = async (client, queryOptions = {}) => {
  const { skip } = queryOptions;
  if (skip) return { data: undefined };
  return client.query(queryOptions);
};

const getFetchPolicy = (hardRefresh = false) => (hardRefresh ? 'no-cache' : 'cache-first');

const getAllProductApps = async (client, { personId, type, subType, hardRefresh }) => {
  const appSummaries = await skippableQuery(client, {
    query: DASHBOARD_GET_ALL_PRODUCT_APPS,
    skip: !personId,
    variables: { personId, types: [type], subTypes: [subType] },
    fetchPolicy: getFetchPolicy(hardRefresh),
  });

  const appIds = _.get(appSummaries.data, 'getAllProductApps', []).map(app => app.id);
  if (!appIds || !appIds.length) return [];

  const allAppResults = await Promise.all(
    appIds.map(productAppId =>
      client.query({
        query: DASHBOARD_GET_PRODUCT_APP,
        variables: { applicationId: productAppId },
      })),
  );

  const allApps = allAppResults
    .map(results => _.get(results.data, 'getProductApp'))
    .filter(x => !!x);

  return allApps;
};

export const usePhase3Data = (personId, type, subType) => {
  const [loading, setLoading] = useState(true);
  const [didTryRegrant, setDidTryRegrant] = useState(false);

  const [phase3Data, setPhase3Data] = useState({
    person: undefined,
    productApplication: undefined,
    edeApplication: undefined,
    enrollmentStatus: [],
    isEnrolled: false,
    allProductApps: [],
  });
  const client = useApolloClient();
  const { regrantPermissionMutation } = useRegrantPermissionMutation();

  const getPhase3Data = useCallback(
    async (options = {}) => {
      const { hardRefresh = false } = options;
      let appId;
      try {
        const [personResult, latestProductAppResult, allProductApps] = await Promise.all([
          skippableQuery(client, {
            query: DASHBOARD_GET_PROFILE,
            skip: !personId,
            variables: { personId },
          }),
          skippableQuery(client, {
            query: DASHBOARD_GET_LATEST_PRODUCT_APP,
            skip: !personId,
            variables: { personId, type, subType },
            fetchPolicy: getFetchPolicy(hardRefresh),
          }),
          getAllProductApps(client, { personId, type, subType, hardRefresh }),
        ]);

        const { data: personData } = personResult;
        const { data: latestProductAppData } = latestProductAppResult;
        const productAppId = _.get(latestProductAppData, 'getLatestProductApp.id');

        const { data: appData } = await skippableQuery(client, {
          query: DASHBOARD_GET_PRODUCT_EDE_APP,
          skip: !productAppId,
          variables: { applicationId: productAppId },
          fetchPolicy: getFetchPolicy(hardRefresh),
        });

        appId = _.get(appData, 'getProductEdeApp.insuranceApplicationIdentifier');

        const { data: enrollStatus } = await skippableQuery(client, {
          skip: !appId,
          query: DASHBOARD_GET_ENROLLMENT_STATUS,
          variables: {
            appId,
          },
          fetchPolicy: getFetchPolicy(hardRefresh),
        });

        const enrollmentStatus = _.get(enrollStatus, 'getEnrollmentStatus', []);
        const isEnrolled =
          enrollmentStatus.length &&
          enrollmentStatus.every((groupStatus) => {
            const status = getStatusType(groupStatus);
            return status !== 'CANCELLED' && status !== 'PEND_CANCELED';
          });

        setPhase3Data({
          person: _.get(personData, 'getPerson'),
          productApplication: _.get(latestProductAppData, 'getLatestProductApp'),
          edeApplication: _.get(appData, 'getProductEdeApp'),
          enrollmentStatus,
          isEnrolled,
          allProductApps,
        });
        setLoading(false);
      } catch (e) {
        // If we get a 403, retry once after regranting permissions
        const statusCodes = getStatusCodes(e);
        if (appId && statusCodes.includes(403) && !didTryRegrant) {
          const { success, error: errorMsg } = await regrantPermissionMutation({ personId, appId });
          setDidTryRegrant(true);

          if (!success) {
            setLoading(false);
            throw new Error(errorMsg || 'Failed to regrant permissions');
          }

          // Refetch - previously successful calls will be cached, only failures will be retried
          getPhase3Data();
        } else {
          setLoading(false);
        }
      }
    },
    [personId, type, subType, client, didTryRegrant],
  );

  useEffect(() => {
    if (personId) {
      getPhase3Data();
    }
  }, [personId, getPhase3Data]);

  return {
    ...phase3Data,
    loading,
    refreshPhase3Data: () => getPhase3Data({ hardRefresh: true }),
  };
};
